import React, { useContext, useEffect, useState } from "react";
import { clearConfigRules, loadConfigRules } from "../lib/configRulesLoader";
import { APP_FLOW_MENU, APP_FLOW_PERMS, APP_MENU, APP_PERMS, TOKEN_NAME } from "../constants/token.const";
import { ApiService } from "@/service/ApiService";
import { GetCookie } from "@/service/CookieService";
import { LoadingSpin } from "@/components/features";
import toast from "react-hot-toast";
import useLoading from "@/components/features/hooks/useLoading";
// import { isMobileUI } from "@/lib/platform";

const AuthContext = React.createContext();

export const formatError = (err) => {
    if (typeof err === "string") return err;
    if (err.data) {
        if (typeof err.data.response === "string") return err.data.response;
        return err.data.response_code;
    }
    return err.statusText;
};

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const { showLoading } = useLoading();
    const [auth, setAuth] = useState({ user: null, loading: true, server_ip: '', txn_date: '' });
    const [isAdminMenu, setIsAdminMenu] = useState(false);
    const [isInstAdmin, setIsInstAdmin] = useState(false);

    function isAuth() {
        if (auth && Object.keys(auth).length === 0) {
            return false;
        }
        return true;
    }

    function onAdminMenuToggle() {
        setIsAdminMenu(!isAdminMenu);
    }

    const api = async (processCode, data, config = {}) => {
        showLoading(true);
        const res = await ApiService(processCode, data, config)
            .then((res) => {
                if (res.status === "error") {
                    return Promise.reject(res);
                }
                return res;
            })
            .catch((err) => {
                if (err?.status === 401) {
                    // redirectTo("login");
                }
                if (Array.isArray(err)) {
                    err.forEach(e => {
                        if (e && typeof e === 'object' && e.message) {
                            const type = e.toastType || 'error';
                            if (type === 'success' || type === 'error') {
                                toast[type](<div dangerouslySetInnerHTML={{ __html: e.message }}></div>, { duration: 10000 });
                            } else {
                                toast(<div dangerouslySetInnerHTML={{ __html: e.message }}></div>, { type: type, duration: 10000 });
                            }
                        } else {
                            toast.error(<div dangerouslySetInnerHTML={{ __html: String(e) }}></div>, { duration: 10000 });
                        }
                    });
                } else {
                    toast.error(<div dangerouslySetInnerHTML={{ __html: err }}></div>);
                }
                // return Promise.reject(err.response);
            })
            .finally(() => showLoading(false));
        return res;
    };

    const getMenu = () => {
        let data = JSON.parse(sessionStorage.getItem(APP_MENU) ?? "");
        let menuArr = [];
        for (let key in data) {
            if (data.hasOwnProperty(key)) {
                menuArr.push(data[key]);
            }
        }
        return menuArr;
    };

    const getFlowMenu = () => {
        const menus = JSON.parse(sessionStorage.getItem(APP_FLOW_MENU)) ?? [];
        const perms = JSON.parse(sessionStorage.getItem(APP_FLOW_PERMS)) ?? [];
        // isadmin бол бүгдийг харуулна
        if (auth?.user?.isadmin === 1) return menus;
        if (!perms || perms.length === 0) return menus; // Дараа хасах
        // Эрхтэй flow code-уудын Set үүсгэнэ
        const allowedCodes = new Set(perms.map(p => p.processcode));

        return menus.filter(item => {
            // typeid='2' (module) цэсийг үргэлж харуулна
            if (item.typeid === '2') return true;
            // typeid='1' эсвэл '3' (flow) цэсийг flowcode-оор шалгана
            return item.flowcode && allowedCodes.has(item.flowcode);
        });
    };

    /**
     * - isadmin === 1 бол үргэлж true
     * - Цэс олдохгүй эсвэл typeid='2' (module) бол нэвтрүүлнэ
     * - flowcode байхгүй бол блок хийнэ
     * - APP_FLOW_PERMS хоосон (эрх байхгүй) бол блок хийнэ
     * - Эс бөгөөс flowcode нь APP_FLOW_PERMS жагсаалтад байгаа эсэхийг шалгана
     */
    const checkFlowPerm = (linkid, typeid = null) => {
        if (auth?.user?.isadmin === 1) return true;
        const perms = JSON.parse(sessionStorage.getItem(APP_FLOW_PERMS)) ?? [];
        const menus = JSON.parse(sessionStorage.getItem(APP_FLOW_MENU)) ?? [];
        
        let menuItem;
        if (typeid) {
            menuItem = menus.find(m => String(m.linkid) === String(linkid) && String(m.typeid) === String(typeid));
        } else {
            menuItem = menus.find(m => String(m.linkid) === String(linkid));
        }

        if (!menuItem) return true;
        if (menuItem.typeid === '2') return true;
        if (!menuItem.flowcode) return false;
        if (!perms || perms.length === 0) return true; // Дараа false болгох

        return perms.some(p => p.processcode === menuItem.flowcode);
    };

    const checkPerm = (pc) => {
        let data = JSON.parse(sessionStorage.getItem(APP_PERMS));
        let res = false;
        data.forEach((item) => {
            if (item.processcode === pc) {
                res = true;
            }
        });
        return res;
    };

    const value = {
        api,
        auth,
        isAuth,
        setAuth,
        getMenu,
        getFlowMenu,
        checkPerm,
        checkFlowPerm,
        onAdminMenuToggle,
        isAdminMenu,
        isInstAdmin,
        setIsInstAdmin,
    };

    useEffect(() => {
        async function checkUser() {
            if (GetCookie(TOKEN_NAME) === undefined || GetCookie(TOKEN_NAME) === null || GetCookie(TOKEN_NAME) === "") {
                setAuth({});
                clearConfigRules();
                return;
            }
            const res = await api("gp000003", {});
            if (res && res?.status !== 'error' && res?.response_code !== 'RC000000') {
                // ⚠ Динамик тохиргоог setAuth-ийн ӨМНӨ бүрэн ачаална.
                //
                // JSON Logic-ийн configParam / configMax г.м. нь СИНХРОН —
                // кэшээс уншдаг. Хэрэв энийг await хийхгүй бол дэлгэц эхэлж
                // render болоод, талбарын event кэш хоосон байхад ажиллаж
                // `null` авдаг (анх ороход утга гарахгүй, refresh хийхээр
                // sessionStorage-оос амжиж сэргээд ажиллана гэсэн гажиг).
                //
                // Татаж чадаагүй ч дотроо залгидаг тул нэвтрэлт зогсохгүй.
                await loadConfigRules();

                setAuth(res);
                localStorage.setItem('ME_LOGINUSER', res.user.userid);
                if (res.user.menus && res.user.menus.hasOwnProperty('ad') && res.user.menus.ad.submenus.length > 0) {
                    setIsInstAdmin(true);
                } else {
                    setIsInstAdmin(false);
                }
                sessionStorage.setItem(APP_MENU, JSON.stringify(res.user.menus))
                sessionStorage.setItem(APP_FLOW_MENU, JSON.stringify(res.user.flowmenus))
                sessionStorage.setItem(APP_PERMS, JSON.stringify(res.user.perms))
                sessionStorage.setItem(APP_FLOW_PERMS, JSON.stringify(res.user.flowperms))
            } else {
                setAuth({})
                clearConfigRules();
            }
        }

        checkUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <AuthContext.Provider value={value}>
        {auth?.loading ? <LoadingSpin /> : children}
    </AuthContext.Provider>;
}

export default AuthContext;
